/**
 * MissionSection.tsx - Mission Statement Configuration Section
 *
 * This component provides an interface for configuring the mission statement
 * in the fundraiser builder details step with title and description editing.
 *
 * Key Features:
 * - Mission title customization
 * - Mission description text area with formatting
 * - Character count and validation
 * - Real-time preview of mission display
 * - Rich text editing capabilities
 * - Template suggestions and examples
 *
 * Configuration Fields:
 * - Mission Title: Customizable section heading
 * - Mission Description: Detailed mission statement text
 * - Character limits and validation
 * - Formatting options for text styling
 *
 * Validation Features:
 * - Required field validation for mission content
 * - Character count limits for optimal display
 * - Text formatting validation
 * - Preview integration for real-time feedback
 *
 * Content Guidelines:
 * - Clear and compelling mission statements
 * - Appropriate length for readability
 * - Consistent tone and messaging
 * - Impact-focused language
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: MobX store, text formatting utilities, validation
 */

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { fundraiserStore } from "../../../stores/FundraiserStore";
import { CreateFundraiserModel } from "../../../models";

const Section = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #e2e8f0;
`;

const FormGrid = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: ${(props) => `repeat(${props.columns || 1}, 1fr)`};
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
`;

const ReducedWidthInput = styled.input`
  width: 50%;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ReducedWidthTextArea = styled.textarea`
  width: 50%;
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

interface MissionSectionProps {
  builderData: CreateFundraiserModel;
  updateBuilderField: (field: keyof CreateFundraiserModel, value: any) => void;
  handleFieldBlur: (fieldName: string) => void;
  onKeyDown?: (e: React.KeyboardEvent, isLastField: boolean) => void;
}

export const MissionSection: React.FC<MissionSectionProps> = observer(
  ({ builderData, updateBuilderField, handleFieldBlur, onKeyDown }) => {
    React.useEffect(() => {
      // Initialize mission fields from fundraiser description if they're empty
      const selectedFundraiser = fundraiserStore.selectedFundraiser;
      if (selectedFundraiser && selectedFundraiser.description) {
        if (!builderData.missionTitle) {
          updateBuilderField("missionTitle", "Our Mission");
        }
        if (!builderData.missionDescription) {
          updateBuilderField(
            "missionDescription",
            selectedFundraiser.description
          );
        }
      }
    }, []);

    return (
      <Section>
        <FormGrid columns={1}>
          <FormGroup>
            <Label htmlFor="missionTitle">Title</Label>
            <ReducedWidthInput
              id="missionTitle"
              type="text"
              placeholder="Enter mission title"
              value={builderData.missionTitle}
              onChange={(e) =>
                updateBuilderField("missionTitle", e.target.value)
              }
              onBlur={() => handleFieldBlur("missionTitle")}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="missionDescription">Description</Label>
            <ReducedWidthTextArea
              id="missionDescription"
              placeholder="Describe your mission and impact"
              value={builderData.missionDescription || ""}
              onChange={(e) =>
                updateBuilderField("missionDescription", e.target.value)
              }
              onBlur={() => handleFieldBlur("missionDescription")}
              onKeyDown={(e) => {
                if (onKeyDown) {
                  onKeyDown(e, true);
                }
              }}
            />
          </FormGroup>
        </FormGrid>
      </Section>
    );
  }
);
